import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Plus, Trash2, Save, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const DEFAULT_CONFIG_VALUES = {
  jenis_event: [
    { value: 'club', label: 'Event Klub' },
    { value: 'school', label: 'Event Sekolah' },
  ],
  format: [
    { value: 'league_round_robin', label: 'Liga Round Robin' },
    { value: 'tournament_elimination', label: 'Tournament Eliminasi' },
  ],
  kategori: [
    { value: 'U-8', label: 'U-8' },
    { value: 'U-10', label: 'U-10' },
    { value: 'U-12', label: 'U-12' },
    { value: 'U-14', label: 'U-14' },
    { value: 'U-17', label: 'U-17' },
    { value: 'U-21', label: 'U-21' },
  ],
  status: [
    { value: 'draft', label: 'Draft' },
    { value: 'open_registration', label: 'Buka Registrasi' },
    { value: 'close_registration', label: 'Tutup Registrasi' },
    { value: 'running', label: 'Berjalan' },
    { value: 'finished', label: 'Selesai' },
  ],
  tipe_liga: [
    { value: 'one_match', label: 'One Match (1x Pertemuan)' },
    { value: 'home_away', label: 'Home Away (2x Pertemuan)' },
  ],
  format_pemain: [
    { value: '7v7', label: '7 vs 7' },
    { value: '11v11', label: '11 vs 11' },
  ],
};

const CONFIG_META = [
  {
    key: 'jenis_event',
    label: 'Jenis Event',
    description: 'Tipe target peserta event (Klub atau Sekolah)',
    hasLabel: true,
  },
  {
    key: 'format',
    label: 'Format',
    description: 'Format kompetisi yang tersedia',
    hasLabel: true,
  },
  {
    key: 'kategori',
    label: 'Kategori',
    description: 'Kategori umur peserta (mis. U-8, U-10, dst)',
    hasLabel: false,
  },
  {
    key: 'status',
    label: 'Status',
    description: 'Status event yang tersedia',
    hasLabel: true,
  },
  {
    key: 'tipe_liga',
    label: 'Tipe Liga',
    description: 'Tipe pertemuan antar tim pada format Liga Round Robin',
    hasLabel: true,
  },
  {
    key: 'format_pemain',
    label: 'Format Pemain',
    description: 'Format jumlah pemain per tim',
    hasLabel: true,
  },
];

const buildDefaultConfigs = () => {
  const result = {};
  CONFIG_META.forEach(({ key }) => {
    result[key] = DEFAULT_CONFIG_VALUES[key] || [];
  });
  return result;
};

const initNewItems = () => {
  const initial = {};
  CONFIG_META.forEach(({ key }) => {
    initial[key] = { value: '', label: '' };
  });
  return initial;
};

const AdminMasterData = () => {
  const [configs, setConfigs] = useState(buildDefaultConfigs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [newItems, setNewItems] = useState(initNewItems);
  const { toast } = useToast();

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await apiService.admin.getAppConfig();
      if (response.data) {
        // Merge server data over defaults (keep defaults for missing keys)
        setConfigs((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Keep defaults already set in state, just show warning
      toast({ title: 'Info', description: 'Menggunakan data default. Perubahan akan disimpan ke server saat klik Simpan.', variant: 'default' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (configKey) => {
    setSaving((prev) => ({ ...prev, [configKey]: true }));
    try {
      await apiService.admin.updateAppConfig(configKey, configs[configKey]);
      toast({ title: 'Berhasil', description: `Master data "${CONFIG_META.find(c => c.key === configKey)?.label}" berhasil disimpan` });
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menyimpan', variant: 'destructive' });
    } finally {
      setSaving((prev) => ({ ...prev, [configKey]: false }));
    }
  };

  const handleAddItem = (configKey, hasLabel) => {
    const item = newItems[configKey];
    if (!item.value.trim()) {
      toast({ title: 'Error', description: 'Value tidak boleh kosong', variant: 'destructive' });
      return;
    }
    const entry = hasLabel
      ? { value: item.value.trim(), label: item.label.trim() || item.value.trim() }
      : { value: item.value.trim(), label: item.value.trim() };

    setConfigs((prev) => ({
      ...prev,
      [configKey]: [...(prev[configKey] || []), entry],
    }));
    setNewItems((prev) => ({
      ...prev,
      [configKey]: hasLabel ? { value: '', label: '' } : { value: '' },
    }));
  };

  const handleRemoveItem = (configKey, idx) => {
    setConfigs((prev) => ({
      ...prev,
      [configKey]: prev[configKey].filter((_, i) => i !== idx),
    }));
  };

  const handleEditLabel = (configKey, idx, field, val) => {
    setConfigs((prev) => {
      const updated = [...(prev[configKey] || [])];
      updated[idx] = { ...updated[idx], [field]: val };
      return { ...prev, [configKey]: updated };
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Memuat master data...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">Master Data Event</h2>
            <p className="text-sm text-gray-500 mt-1">Kelola opsi-opsi yang tersedia saat membuat event</p>
          </div>
          <button
            onClick={loadConfigs}
            className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {CONFIG_META.map(({ key, label, description, hasLabel }) => {
            const items = configs[key] || [];
            const newItem = newItems[key] || {};
            return (
              <div key={key} className="bg-white rounded-lg shadow-md p-4 sm:p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-gray-900">{label}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                  </div>
                  <button
                    onClick={() => handleSave(key)}
                    disabled={saving[key]}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg disabled:opacity-50"
                  >
                    <Save className="h-3.5 w-3.5" />
                    {saving[key] ? 'Menyimpan...' : 'Simpan'}
                  </button>
                </div>

                {/* Existing items */}
                <div className="space-y-2 mb-4">
                  {items.length === 0 && (
                    <p className="text-sm text-gray-400 italic">Belum ada item</p>
                  )}
                  {items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={item.value}
                          onChange={(e) => handleEditLabel(key, idx, 'value', e.target.value)}
                          placeholder="Value"
                          className="text-sm px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                        <input
                          type="text"
                          value={item.label}
                          onChange={(e) => handleEditLabel(key, idx, 'label', e.target.value)}
                          placeholder="Label tampil"
                          className="text-sm px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveItem(key, idx)}
                        className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new item */}
                <div className="flex items-center gap-2 border-t pt-3">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={newItem.value || ''}
                      onChange={(e) =>
                        setNewItems((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], value: e.target.value },
                        }))
                      }
                      onKeyDown={(e) => e.key === 'Enter' && handleAddItem(key, hasLabel)}
                      placeholder="Value baru"
                      className="text-sm px-2 py-1.5 border rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                    <input
                      type="text"
                      value={newItem.label || ''}
                      onChange={(e) =>
                        setNewItems((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], label: e.target.value },
                        }))
                      }
                      onKeyDown={(e) => e.key === 'Enter' && handleAddItem(key, hasLabel)}
                      placeholder="Label tampil (opsional)"
                      className="text-sm px-2 py-1.5 border rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                  </div>
                  <button
                    onClick={() => handleAddItem(key, hasLabel)}
                    className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMasterData;
